/**
 * What a person is told when a spend limit stopped their work.
 *
 * ONE definition, deliberately. These sentences are load-bearing: each satisfies
 * three separate obligations -- name whose limit it was, do NOT blame the
 * organization when it was the member's, and name the remedy. A second copy
 * would satisfy them on the day it was written and drift afterwards, and the two
 * would be visible to the same person on different screens.
 *
 * Dependency-free on purpose. The server path reaches this through
 * `llm-gateway.server.ts`, which pulls in LangChain; the chat transport is a
 * CLIENT module and importing that file would drag the whole SDK into the
 * browser bundle. So the words live here, where both can reach them and neither
 * brings anything with it.
 */

/** Which spend limit refused the work. */
export type SpendLimitPayer = "member" | "organization" | "platform";

/**
 * The sentence shown for a refusal.
 *
 * The member case names the person's OWN limit and points at an administrator.
 * Telling them the organization is out of budget -- which is what happened
 * before the two refusals were told apart -- sends them to wait out a month that
 * was never the problem, while the thing that would actually fix it goes
 * unasked.
 *
 * None of them invites a retry. Neither limit can change inside a request, so
 * "try again" is the one instruction guaranteed not to work.
 */
export function spendLimitMessage(payer: SpendLimitPayer): string {
  switch (payer) {
    case "member":
      return (
        "You have reached your own spend limit for the current period. " +
        "Your organization may still have budget: an organization " +
        "administrator can raise or remove your limit."
      );
    case "organization":
      return "This organization has reached its spend limit for the current period.";
    default:
      return "The platform has reached its spend limit for the current period.";
  }
}

/** The error payload a failed stream carries. Fields beyond `content` are additive. */
type StreamErrorData = {
  content?: unknown;
  reason?: unknown;
  limit?: unknown;
};

/**
 * What to show for a failed stream, naming a spend limit when one caused it.
 *
 * The workflow service sends the KIND (`reason: "spend-limit"`, `limit`); the
 * wording is chosen here so it matches what the rest of the application says for
 * the same refusal. Anything else falls back to whatever the stream reported,
 * and then to a generic failure -- but never to nothing, because showing nothing
 * is the defect this exists to fix.
 */
export function streamErrorMessage(data: StreamErrorData | undefined): string {
  if (data?.reason === "spend-limit") {
    const limit = data.limit;
    if (limit === "member" || limit === "organization") {
      return spendLimitMessage(limit);
    }
  }
  const content = data?.content;
  if (typeof content === "string" && content.trim().length > 0) return content;
  return "Something went wrong and this request could not be completed.";
}
